/**
 * In-App Notification System Models
 *
 * This file contains all TypeScript interfaces for the notification system.
 * Collection: users/{uid}/notifications/{notificationId}
 *
 * @see docs/features/IN-APP-NOTIFICATION-SYSTEM-SPEC.md
 */

import { Timestamp } from 'firebase/firestore';

// ============================================================================
// NOTIFICATION TYPES
// ============================================================================

/**
 * Categories of notifications
 */
export type NotificationCategory =
    | 'policy' // Policy-related notifications
    | 'approval' // Approval workflow notifications
    | 'system' // System announcements
    | 'task' // Task assignments
    | 'mention' // User mentions
    | 'collaboration' // Workspace collaboration
    | 'security' // Security alerts
    | 'reminder' // Scheduled reminders
    | 'invitation' // Organization invitation lifecycle events
    | 'statistics'; // Sales statistics (the end-of-day summary)

/**
 * Specific notification types
 */
export type NotificationType =
    // Policy notifications
    | 'policy_published' // New policy published
    | 'policy_requires_acknowledgment' // Policy needs your acknowledgment
    | 'policy_acknowledgment_reminder' // Reminder to acknowledge
    | 'policy_deadline_approaching' // Deadline is near
    | 'policy_deadline_passed' // Deadline has passed
    | 'policy_updated' // Policy you've seen was updated
    | 'policy_revoked' // Policy was revoked
    | 'policy_expired' // Policy has expired

    // Mailbox health (Phase 6 silent-lapse monitor)
    | 'mailbox_sync_lapsed' // Linked mailbox stopped ingesting mail

    // Statistics (OE-265 idea 7)
    | 'statistics_daily_summary' // The day's sales at closing

    // Approval notifications
    | 'approval_requested' // You need to approve something
    | 'approval_approved' // Your request was approved
    | 'approval_rejected' // Your request was rejected
    | 'approval_escalated' // Approval escalated to you
    | 'approval_reminder' // Reminder to approve

    // Task notifications
    | 'task_assigned' // Task assigned to you
    | 'task_due_soon' // Task due date approaching
    | 'task_overdue' // Task is overdue
    | 'task_completed' // Task was completed

    // Collaboration notifications
    | 'document_shared' // Document shared with you
    | 'comment_added' // Comment on your document
    | 'mentioned_in_document' // You were mentioned

    // System notifications
    | 'system_announcement' // System-wide announcement
    | 'system_maintenance' // Maintenance notification
    | 'welcome' // Welcome message

    // Security notifications
    | 'security_alert' // Security-related alert
    | 'password_change' // Password change notification
    | 'new_device_login' // Login from new device

    // Invitation notifications (Phase B — staff stage)
    | 'invitation_declined' // Inviter notified: a wizard-staged invite was declined
    | 'invitation_received' // Invitee notified: in-app accept card (B.4)

    // Issue tracker v2 (ISSUE-TRACKER-V2-DESIGN.md §3) — comment_added / mentioned_in_document /
    // task_assigned are reused for comments, @mentions and assignment; these two are the
    // tracker-specific events that had no honest existing member.
    | 'issue_status_changed' // An issue you reported / own moved column
    | 'issue_archived' // An issue you reported / own was archived or restored
    | 'issue_updated'; // Any other change to an issue you reported / own (ISS-026)

/**
 * Notification priority levels
 */
export type NotificationPriority =
    | 'low' // Informational, can be batched
    | 'normal' // Standard priority
    | 'high' // Important, show prominently
    | 'urgent'; // Critical, may trigger push/email

// ============================================================================
// CORE NOTIFICATION INTERFACE
// ============================================================================

/**
 * In-App Notification
 * Collection: users/{uid}/notifications/{notificationId}
 */
export interface InAppNotification {
    /** Unique notification ID */
    key: string;

    /** User who receives this notification */
    recipientUid: string;

    /** Organization context (if applicable) */
    orgKey?: string;

    // === Content ===
    /** Notification category */
    category: NotificationCategory;

    /** Specific notification type */
    type: NotificationType;

    /** Short title */
    title: string;

    /** Notification message/body */
    message: string;

    /** Optional preview text (for truncated display) */
    preview?: string;

    // === Display ===
    /** Priority level */
    priority: NotificationPriority;

    /** Icon name (Material icon) */
    icon?: string;

    /** Icon color/class */
    iconColor?: 'primary' | 'accent' | 'warn' | 'success' | 'info';

    /** Avatar URL (for user-related notifications) */
    avatarUrl?: string;

    // === Navigation ===
    /** Route to navigate to when clicked */
    actionRoute?: string;

    /** Query params for the route */
    actionQueryParams?: Record<string, string>;

    /** Action button text (if not default "View") */
    actionText?: string;

    // === Related Entity ===
    /** Type of related entity */
    relatedEntityType?: 'policy' | 'task' | 'document' | 'user' | 'approval';

    /** Key of the related entity */
    relatedEntityKey?: string;

    /** Name/title of the related entity (for display) */
    relatedEntityName?: string;

    // === Sender ===
    /** Who/what generated this notification */
    senderType: 'system' | 'user' | 'cron';

    /** Sender user UID (if senderType is 'user') */
    senderUid?: string;

    /** Sender display name */
    senderName?: string;

    // === Status ===
    /** Has the notification been read */
    isRead: boolean;

    /** When it was read */
    readAt?: Timestamp;

    /** Has it been dismissed */
    isDismissed: boolean;

    /** When it was dismissed */
    dismissedAt?: Timestamp;

    /** Has the user clicked/actioned it */
    isActioned: boolean;

    /** When it was actioned */
    actionedAt?: Timestamp;

    // === Timing ===
    /** When the notification was created */
    createdAt: Timestamp;

    /** When it expires (auto-cleanup) */
    expiresAt?: Timestamp;

    /** For scheduled notifications - when to show */
    scheduledFor?: Timestamp;

    // === Metadata ===
    /** Additional custom data */
    metadata?: Record<string, unknown>;

    /** Grouping key (for collapsing similar notifications) */
    groupKey?: string;

    /** Batch ID (for bulk notifications) */
    batchId?: string;
}

// ============================================================================
// USER NOTIFICATION PREFERENCES
// ============================================================================

/**
 * User notification preferences
 * Collection: users/{uid} (nested field) OR users/{uid}/settings/notifications
 */
export interface NotificationPreferences {
    /** Master switch for all notifications */
    enabled: boolean;

    /** Enable email notifications */
    emailEnabled: boolean;

    /** Email digest frequency */
    emailDigestFrequency: 'immediate' | 'daily' | 'weekly' | 'never';

    /** Enable browser push notifications */
    pushEnabled: boolean;

    /** Enable notification sounds */
    soundEnabled: boolean;

    /** Quiet hours (no notifications during these times) */
    quietHours?: {
        enabled: boolean;
        startTime: string; // HH:mm format
        endTime: string; // HH:mm format
        timezone: string;
    };

    /** Category-specific settings */
    categorySettings: {
        [K in NotificationCategory]?: {
            /** Enable this category */
            enabled: boolean;
            /** Send emails for this category */
            emailEnabled: boolean;
            /** Show desktop push for this category */
            pushEnabled: boolean;
        };
    };
}

/**
 * Default notification preferences
 */
export const DEFAULT_NOTIFICATION_PREFERENCES: NotificationPreferences = {
    enabled: true,
    emailEnabled: true,
    emailDigestFrequency: 'immediate',
    pushEnabled: false,
    soundEnabled: true,
    quietHours: {
        enabled: false,
        startTime: '22:00',
        endTime: '07:00',
        timezone: 'UTC',
    },
    categorySettings: {
        policy: { enabled: true, emailEnabled: true, pushEnabled: true },
        approval: { enabled: true, emailEnabled: true, pushEnabled: true },
        system: { enabled: true, emailEnabled: false, pushEnabled: false },
        task: { enabled: true, emailEnabled: true, pushEnabled: true },
        mention: { enabled: true, emailEnabled: true, pushEnabled: true },
        collaboration: { enabled: true, emailEnabled: false, pushEnabled: false },
        security: { enabled: true, emailEnabled: true, pushEnabled: true },
        reminder: { enabled: true, emailEnabled: true, pushEnabled: false },
        statistics: { enabled: true, emailEnabled: true, pushEnabled: false },
    },
};

// ============================================================================
// NOTIFICATION BATCH/GROUP
// ============================================================================

/**
 * Notification batch for bulk sending
 * Collection: organizations/{orgId}/notificationBatches/{batchId}
 */
export interface NotificationBatch {
    /** Unique batch ID */
    key: string;

    /** Organization key */
    orgKey: string;

    /** Batch name/description */
    name: string;

    /** Notification template */
    template: Omit<InAppNotification, 'key' | 'recipientUid' | 'createdAt' | 'isRead' | 'isDismissed' | 'isActioned'>;

    /** Target user UIDs */
    recipientUids: string[];

    /** Total recipients */
    totalRecipients: number;

    /** Number sent */
    sentCount: number;

    /** Number failed */
    failedCount: number;

    /** Batch status */
    status: 'pending' | 'in_progress' | 'completed' | 'failed' | 'cancelled';

    /** When batch was created */
    createdAt: Timestamp;

    /** Who created the batch */
    createdBy: string;

    /** When processing started */
    startedAt?: Timestamp;

    /** When processing completed */
    completedAt?: Timestamp;

    /** Error message if failed */
    errorMessage?: string;
}

// ============================================================================
// NOTIFICATION TEMPLATES
// ============================================================================

/**
 * Predefined notification templates
 */
export interface NotificationTemplate {
    /** Template key */
    key: string;

    /** Template name */
    name: string;

    /** Notification type this template is for */
    type: NotificationType;

    /** Category */
    category: NotificationCategory;

    /** Title template (supports {{variable}} placeholders) */
    titleTemplate: string;

    /** Message template (supports {{variable}} placeholders) */
    messageTemplate: string;

    /** Default icon */
    icon: string;

    /** Default icon color */
    iconColor: 'primary' | 'accent' | 'warn' | 'success' | 'info';

    /** Default priority */
    priority: NotificationPriority;

    /** Route template */
    actionRouteTemplate?: string;

    /** Whether this template is active */
    isActive: boolean;
}

/**
 * Built-in notification templates
 */
export const NOTIFICATION_TEMPLATES: Record<NotificationType, Partial<NotificationTemplate>> = {
    // Statistics
    statistics_daily_summary: {
        titleTemplate: 'Sales today: {{nett}}',
        messageTemplate: '{{summary}}',
        icon: 'insights',
        iconColor: 'primary',
        priority: 'normal',
    },

    // Mailbox health
    mailbox_sync_lapsed: {
        titleTemplate: 'Email sync may have stopped',
        messageTemplate: '{{email}} has stopped receiving mail. The connection may need re-linking.',
        icon: 'sync_problem',
        iconColor: 'warn',
        priority: 'high',
    },

    // Policy templates
    policy_published: {
        titleTemplate: 'New Policy: {{policyName}}',
        messageTemplate: 'A new policy "{{policyName}}" has been published.',
        icon: 'policy',
        iconColor: 'primary',
        priority: 'normal',
    },
    policy_requires_acknowledgment: {
        titleTemplate: 'Action Required: {{policyName}}',
        messageTemplate: 'Please review and acknowledge the policy "{{policyName}}".',
        icon: 'assignment_turned_in',
        iconColor: 'warn',
        priority: 'high',
    },
    policy_acknowledgment_reminder: {
        titleTemplate: 'Reminder: {{policyName}}',
        messageTemplate: 'Reminder: Please acknowledge the policy "{{policyName}}" by {{deadline}}.',
        icon: 'notification_important',
        iconColor: 'warn',
        priority: 'high',
    },
    policy_deadline_approaching: {
        titleTemplate: 'Deadline Approaching: {{policyName}}',
        messageTemplate: 'The deadline to acknowledge "{{policyName}}" is {{deadline}}.',
        icon: 'schedule',
        iconColor: 'warn',
        priority: 'high',
    },
    policy_deadline_passed: {
        titleTemplate: 'Deadline Passed: {{policyName}}',
        messageTemplate: 'The deadline to acknowledge "{{policyName}}" has passed. Please complete immediately.',
        icon: 'warning',
        iconColor: 'warn',
        priority: 'urgent',
    },
    policy_updated: {
        titleTemplate: 'Policy Updated: {{policyName}}',
        messageTemplate: 'The policy "{{policyName}}" has been updated to version {{version}}.',
        icon: 'update',
        iconColor: 'info',
        priority: 'normal',
    },
    policy_revoked: {
        titleTemplate: 'Policy Revoked: {{policyName}}',
        messageTemplate: 'The policy "{{policyName}}" has been revoked. Reason: {{reason}}',
        icon: 'block',
        iconColor: 'warn',
        priority: 'high',
    },
    policy_expired: {
        titleTemplate: 'Policy Expired: {{policyName}}',
        messageTemplate: 'The policy "{{policyName}}" has expired.',
        icon: 'event_busy',
        iconColor: 'accent',
        priority: 'low',
    },

    // Approval templates
    approval_requested: {
        titleTemplate: 'Approval Requested',
        messageTemplate: '{{requesterName}} has requested your approval for "{{itemName}}".',
        icon: 'approval',
        iconColor: 'primary',
        priority: 'high',
    },
    approval_approved: {
        titleTemplate: 'Request Approved',
        messageTemplate: 'Your request "{{itemName}}" has been approved by {{approverName}}.',
        icon: 'check_circle',
        iconColor: 'success',
        priority: 'normal',
    },
    approval_rejected: {
        titleTemplate: 'Request Rejected',
        messageTemplate: 'Your request "{{itemName}}" was rejected by {{approverName}}. Reason: {{reason}}',
        icon: 'cancel',
        iconColor: 'warn',
        priority: 'high',
    },
    approval_escalated: {
        titleTemplate: 'Approval Escalated',
        messageTemplate: 'An approval for "{{itemName}}" has been escalated to you.',
        icon: 'priority_high',
        iconColor: 'warn',
        priority: 'urgent',
    },
    approval_reminder: {
        titleTemplate: 'Approval Reminder',
        messageTemplate: 'Reminder: Please review the approval request for "{{itemName}}".',
        icon: 'notification_important',
        iconColor: 'warn',
        priority: 'high',
    },

    // Task templates
    task_assigned: {
        titleTemplate: 'New Task Assigned',
        messageTemplate: '{{assignerName}} assigned you the task "{{taskName}}".',
        icon: 'assignment_ind',
        iconColor: 'primary',
        priority: 'normal',
    },
    task_due_soon: {
        titleTemplate: 'Task Due Soon',
        messageTemplate: 'The task "{{taskName}}" is due {{dueDate}}.',
        icon: 'schedule',
        iconColor: 'warn',
        priority: 'high',
    },
    task_overdue: {
        titleTemplate: 'Task Overdue',
        messageTemplate: 'The task "{{taskName}}" is overdue.',
        icon: 'warning',
        iconColor: 'warn',
        priority: 'urgent',
    },
    task_completed: {
        titleTemplate: 'Task Completed',
        messageTemplate: 'The task "{{taskName}}" has been completed.',
        icon: 'task_alt',
        iconColor: 'success',
        priority: 'low',
    },

    // Collaboration templates
    document_shared: {
        titleTemplate: 'Document Shared',
        messageTemplate: '{{sharerName}} shared "{{documentName}}" with you.',
        icon: 'share',
        iconColor: 'primary',
        priority: 'normal',
    },
    comment_added: {
        titleTemplate: 'New Comment',
        messageTemplate: '{{commenterName}} commented on "{{documentName}}".',
        icon: 'comment',
        iconColor: 'primary',
        priority: 'normal',
    },
    mentioned_in_document: {
        titleTemplate: 'You Were Mentioned',
        messageTemplate: '{{mentionerName}} mentioned you in "{{documentName}}".',
        icon: 'alternate_email',
        iconColor: 'accent',
        priority: 'high',
    },

    // System templates
    system_announcement: {
        titleTemplate: 'System Announcement',
        messageTemplate: '{{message}}',
        icon: 'campaign',
        iconColor: 'primary',
        priority: 'normal',
    },
    system_maintenance: {
        titleTemplate: 'Scheduled Maintenance',
        messageTemplate: 'System maintenance scheduled for {{maintenanceTime}}.',
        icon: 'build',
        iconColor: 'warn',
        priority: 'high',
    },
    welcome: {
        titleTemplate: 'Welcome to {{appName}}!',
        messageTemplate: 'Welcome aboard! Get started by exploring your workspace.',
        icon: 'celebration',
        iconColor: 'success',
        priority: 'low',
    },

    // Security templates
    security_alert: {
        titleTemplate: 'Security Alert',
        messageTemplate: '{{message}}',
        icon: 'security',
        iconColor: 'warn',
        priority: 'urgent',
    },
    password_change: {
        titleTemplate: 'Password Changed',
        messageTemplate: 'Your password was successfully changed.',
        icon: 'lock',
        iconColor: 'success',
        priority: 'normal',
    },
    new_device_login: {
        titleTemplate: 'New Device Login',
        messageTemplate: 'A new device logged into your account from {{location}}.',
        icon: 'devices',
        iconColor: 'warn',
        priority: 'high',
    },

    // Invitation templates (Phase B — staff stage)
    invitation_declined: {
        titleTemplate: 'Invitation declined',
        messageTemplate: '{{inviteeEmail}} declined the invitation to "{{orgName}}".',
        icon: 'person_off',
        iconColor: 'accent',
        priority: 'low',
    },
    invitation_received: {
        titleTemplate: 'You have an invitation',
        messageTemplate: '{{inviterName}} invited you to join "{{orgName}}".',
        icon: 'mail',
        iconColor: 'primary',
        priority: 'normal',
    },

    // Issue tracker v2 (client-written into the central project; ISSUE-TRACKER-V2-DESIGN.md §3)
    issue_status_changed: {
        titleTemplate: '{{issueKey}} · {{issueTitle}}',
        messageTemplate: '{{actorName}} moved this to {{status}}.',
        icon: 'swap_horiz',
        iconColor: 'primary',
        priority: 'normal',
    },
    issue_archived: {
        titleTemplate: '{{issueKey}} · {{issueTitle}}',
        messageTemplate: '{{actorName}} {{action}} this issue.',
        icon: 'archive',
        iconColor: 'accent',
        priority: 'low',
    },
    issue_updated: {
        titleTemplate: '{{issueKey}} · {{issueTitle}}',
        messageTemplate: '{{actorName}} {{change}}.',
        icon: 'edit',
        iconColor: 'primary',
        priority: 'normal',
    },
};

// ============================================================================
// NOTIFICATION COUNT/BADGE
// ============================================================================

/**
 * Notification counts for badge display
 * Can be cached on user document for performance
 */
export interface NotificationCounts {
    /** Total unread notifications */
    unread: number;

    /** Unread by category */
    unreadByCategory: {
        [K in NotificationCategory]?: number;
    };

    /** High priority unread count */
    unreadHighPriority: number;

    /** Urgent unread count */
    unreadUrgent: number;

    /** Last updated timestamp */
    lastUpdated: Timestamp;
}

// ============================================================================
// HELPER FUNCTIONS - Type Guards
// ============================================================================

/**
 * Check if notification is policy-related
 */
export function isPolicyNotification(notification: InAppNotification): boolean {
    return notification.category === 'policy';
}

/**
 * Check if notification requires action
 */
export function requiresAction(notification: InAppNotification): boolean {
    return notification.priority === 'high' || notification.priority === 'urgent';
}

/**
 * Check if notification is expired
 */
export function isNotificationExpired(notification: InAppNotification): boolean {
    if (!notification.expiresAt) return false;
    return notification.expiresAt.toDate() < new Date();
}
